<template>
  <section id="fleet" class="py-16 bg-gray-100">
    <div class="max-w-7xl mx-auto px-4">
      <div class="fade-in-section">
        <h2 class="text-3xl sm:text-4xl font-bold text-center text-teal-800 mb-4">{{ $t('fleet.title') }}</h2>
        <p class="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{{ $t('fleet.subtitle') }}</p>
      </div>

      <!-- Fleet Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div 
          v-for="(vehicle, index) in vehicles" 
          :key="index"
          class="bg-white rounded-2xl shadow-lg overflow-hidden fade-in-section hover:shadow-xl transition-shadow duration-300"
        >
          <!-- Image Gallery -->
          <div class="relative h-64 md:h-80 overflow-hidden">
            <img 
              :src="vehicle.images[vehicle.activeImage]" 
              :alt="vehicle.name"
              class="w-full h-full object-cover transition-transform duration-500"
            />
            
            <!-- Image Navigation Dots -->
            <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              <button
                v-for="(img, imgIndex) in vehicle.images"
                :key="imgIndex"
                @click="setActiveImage(index, imgIndex)"
                class="w-2.5 h-2.5 rounded-full transition-all duration-300"
                :class="vehicle.activeImage === imgIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80'"
              ></button>
            </div>

            <!-- Prev/Next Arrows -->
            <button 
              @click="prevImage(index)"
              class="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <button 
              @click="nextImage(index)"
              class="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>

            <!-- Image Counter -->
            <div class="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {{ vehicle.activeImage + 1 }} / {{ vehicle.images.length }}
            </div>
          </div>

          <!-- Vehicle Info -->
          <div class="p-6">
            <h3 class="text-2xl font-bold text-teal-800 mb-2">{{ vehicle.name }}</h3>
            <p class="text-gray-600 mb-4">{{ $t(vehicle.descriptionKey) }}</p>

            <!-- Specs Grid -->
            <div class="grid grid-cols-2 gap-3 mb-4">
              <div class="flex items-center text-gray-700">
                <svg class="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
                <span><strong>{{ vehicle.seats }}</strong> {{ $t('fleet.seats') }}</span>
              </div>
              <div v-if="vehicle.year" class="flex items-center text-gray-700">
                <svg class="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span>{{ $t('fleet.year') }}: <strong>{{ vehicle.year }}</strong></span>
              </div>
            </div>

            <!-- Services Icons -->
            <div class="flex flex-wrap gap-3">
              <div 
                v-for="(serviceKey, sIndex) in vehicle.servicesKeys" 
                :key="sIndex"
                class="flex items-center bg-gray-100 px-3 py-1.5 rounded-full text-sm text-gray-700"
              >
                <span class="mr-1.5">{{ getServiceIcon(serviceKey) }}</span>
                {{ $t(serviceKey) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FleetSection',
  data() {
    return {
      // Configura qui i tuoi mezzi
      vehicles: [
        {
          name: 'MAN 64 posti',
          descriptionKey: 'fleet.vehicle1Description',
          seats: 64,
          activeImage: 0,
          images: [
            require('@/assets/img_flotta/veicolo1/lungo fuori.jpeg'),
            require('@/assets/img_flotta/veicolo1/lungo dentro.jpeg'),
          ],
          servicesKeys: ['fleet.airConditioning', 'fleet.tvMonitor', 'fleet.audioStereo', 'fleet.recliningSeats']
        },
        {
          name: 'Irizar',
          descriptionKey: 'fleet.vehicle2Description',
          seats: 20,
          activeImage: 0,
          images: [
            require('@/assets/img_flotta/veicolo2/irizar fuori.jpeg'),
            require('@/assets/img_flotta/veicolo2/irizar dentro.jpeg'),
          ],
          servicesKeys: ['fleet.airConditioning', 'fleet.audioStereo', 'fleet.comfortSeats']
        },
      ]
    };
  },
  methods: {
    getServiceIcon(serviceKey) {
      const icons = {
        'fleet.airConditioning': '❄️',
        'fleet.tvMonitor': '📺',
        'fleet.audioStereo': '🎵',
        'fleet.recliningSeats': '💺',
        'fleet.comfortSeats': '💺',
        'fleet.toilet': '🚻',
        'fleet.wifi': '📶'
      };
      return icons[serviceKey] || '✓';
    },
    setActiveImage(vehicleIndex, imageIndex) {
      this.vehicles[vehicleIndex].activeImage = imageIndex;
    },
    nextImage(vehicleIndex) {
      const vehicle = this.vehicles[vehicleIndex];
      vehicle.activeImage = (vehicle.activeImage + 1) % vehicle.images.length;
    },
    prevImage(vehicleIndex) {
      const vehicle = this.vehicles[vehicleIndex];
      vehicle.activeImage = (vehicle.activeImage - 1 + vehicle.images.length) % vehicle.images.length;
    }
  }
};
</script>

<style scoped>
</style>
